import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FISH_RULES, type FinfishRule, type ShellfishRule } from '../data/fishRules';
import { ID_NOTES } from '../data/fishIdNotes';

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', href);
}

const AREAS = [
  { key: 'Auckland & Kermadec (FMA1)', short: 'Auckland' },
  { key: 'Central (FMA2/8)', short: 'Central' },
  { key: 'Challenger (FMA7)', short: 'Challenger' },
  { key: 'Chatham Rise (FMA4)', short: 'Chatham Rise' },
  { key: 'Fiordland (FMA5)', short: 'Fiordland' },
  { key: 'Kaikoura', short: 'Kaikōura' },
  { key: 'South-East (FMA3)', short: 'South-East' },
  { key: 'Southland/Sub-Antarctic (FMA6)', short: 'Southland' },
];

const slug = (n: string) => n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const IMAGE_OVERRIDES: Record<string, string> = {
  'Blue shark': 'rig',
  'Bluenose': 'groper-hapuku-bass',
  'Bronze shark': 'rig',
  'Butterfish (Greenbone)': 'blue-moki',
  'Flatfish (except Sand flounder)': 'sand-flounder',
  'Grey mullet': 'kahawai',
  'Hammerhead shark': 'rig',
  'Hoki': 'groper-hapuku-bass',
  'Lamprey': 'rig',
  'Ling': 'groper-hapuku-bass',
  'Mako shark': 'rig',
  'Marblefish': 'blue-moki',
  'Parore': 'trevally',
  'Porbeagle shark': 'rig',
  'Quinnat salmon': 'albacore-tuna',
  'Red cod': 'blue-cod',
  'Red moki': 'blue-moki',
  'School shark': 'rig',
  'Sea perch/Jock Stewart': 'red-gurnard',
  'Seven gilled shark': 'rig',
  'Skate/Ray': 'sand-flounder',
  'Southern bluefin tuna': 'albacore-tuna',
  'Spiny dogfish': 'rig',
  'Spotted black groper': 'groper-hapuku-bass',
  'Stargazer': 'red-gurnard',
  'Thresher shark': 'rig',
  'Warehou': 'trevally',
  'White pointer shark': 'rig',
  'Wrasse': 'blue-moki',
  'All others (combined)': 'cockle',
  'Oysters - Rock and Pacific': 'oysters-dredge',
  'Pupu (Cats eye)': 'cockle',
  'Toheroa': 'tuatua',
};

const FEATURED_BY_AREA: Record<string, string[]> = {
  'Auckland & Kermadec (FMA1)': ['Snapper', 'Kahawai', 'Kingfish', 'Trevally', 'Tarakihi', 'Red gurnard'],
  'Central (FMA2/8)': ['Snapper', 'Kahawai', 'Kingfish', 'Tarakihi', 'Red gurnard', 'Paua - Ordinary'],
  'Challenger (FMA7)': ['Snapper', 'Blue cod', 'Kahawai', 'Kingfish', 'Scallops', 'Paua - Ordinary'],
  'Chatham Rise (FMA4)': ['Blue cod', 'Groper (Hapuku)/Bass', 'Tarakihi', 'Trumpeter', 'Spiny rock lobster (crayfish)', 'Paua - Ordinary'],
  'Fiordland (FMA5)': ['Blue cod', 'Groper (Hapuku)/Bass', 'Trumpeter', 'Blue moki', 'Spiny rock lobster (crayfish)', 'Paua - Ordinary'],
  'Kaikoura': ['Blue cod', 'Groper (Hapuku)/Bass', 'Kingfish', 'Tarakihi', 'Paua - Ordinary', 'Spiny rock lobster (crayfish)'],
  'South-East (FMA3)': ['Blue cod', 'Groper (Hapuku)/Bass', 'Trumpeter', 'Tarakihi', 'Paua - Ordinary', 'Spiny rock lobster (crayfish)'],
  'Southland/Sub-Antarctic (FMA6)': ['Blue cod', 'Trumpeter', 'Groper (Hapuku)/Bass', 'Paua - Ordinary', 'Scallops', 'Oysters - Dredge'],
};

const AREA_MAP_POINTS = [
  { key: 'Auckland & Kermadec (FMA1)', label: 'FMA1', x: 63, y: 23 },
  { key: 'Central (FMA2/8)', label: 'FMA2/8', x: 48, y: 47 },
  { key: 'Challenger (FMA7)', label: 'FMA7', x: 31, y: 72 },
  { key: 'Chatham Rise (FMA4)', label: 'FMA4', x: 80, y: 79 },
  { key: 'Fiordland (FMA5)', label: 'FMA5', x: 26, y: 101 },
  { key: 'Kaikoura', label: 'Kaikoura', x: 48, y: 78 },
  { key: 'South-East (FMA3)', label: 'FMA3', x: 49, y: 96 },
  { key: 'Southland/Sub-Antarctic (FMA6)', label: 'FMA6', x: 41, y: 116 },
];

const imageSlug = (name: string) => IMAGE_OVERRIDES[name] || slug(name);

function fmtBag(b: number | string | null | undefined): string {
  if (b === null || b === undefined) return '—';
  if (b === 'none') return 'No limit';
  if (b === 'prohibited') return 'CLOSED';
  return String(b);
}

function resolveFinfish(s: FinfishRule, area: string) {
  const ex = (s.regional_exceptions || []).find(e => e.area === area);
  return {
    min: ex?.min_length_cm ?? s.min_length_cm,
    bag: ex?.bag_limit ?? s.bag_limit,
    note: ex?.notes || s.notes || '',
    areaSpecific: !!ex,
  };
}

function resolveShellfish(s: ShellfishRule, area: string) {
  const ex = (s.regional_exceptions || []).find(e => e.area === area);
  const seasonEntry = (s.season || '').split(';').map(t => t.trim()).find(t => t.startsWith(area));
  const season = seasonEntry ? seasonEntry.split(':').slice(1).join(':').trim() : '';
  return {
    size: ex?.min_size_mm ?? s.min_size_mm,
    bag: ex?.bag_limit ?? s.bag_limit,
    note: ex?.notes || '',
    season,
    areaSpecific: !!ex,
  };
}

function SpeciesImage({ name }: { name: string }) {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-50 to-blue-100 p-4 text-center text-xs font-bold text-text-muted dark:from-cyan-950/40 dark:to-blue-900/30">
        Image coming soon
      </div>
    );
  }
  return (
    <img
      src={`/images/fish/${imageSlug(name)}.png`}
      alt={`${name} identification illustration`}
      loading="lazy"
      onError={() => setErr(true)}
      className="w-full h-full object-cover"
    />
  );
}

function Badge({ children, tone = 'default' }: { children: ReactNode; tone?: 'default' | 'area' | 'warn' }) {
  const styles = {
    default: 'bg-accent/10 text-accent border-accent/20',
    area: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25',
    warn: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/25',
  }[tone];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[0.65rem] font-bold border ${styles}`}>
      {children}
    </span>
  );
}

function FinfishCard({ species, area }: { species: FinfishRule; area: string }) {
  const r = resolveFinfish(species, area);
  const idNote = ID_NOTES[species.common_name];
  return (
    <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] bg-surface-light dark:bg-surface overflow-hidden shadow-sm flex flex-col">
      <div className="aspect-[16/10] bg-[rgba(6,182,212,0.05)]">
        <SpeciesImage name={species.common_name} />
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div>
          <h3 className="font-display font-extrabold text-base leading-tight">{species.common_name}</h3>
          <p className="text-text-muted text-xs italic">{species.scientific_name}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={r.min === null ? 'warn' : 'default'}>
            {r.min === null ? 'No min size' : `Min ${r.min} cm`}
          </Badge>
          <Badge tone={r.bag === 'prohibited' ? 'warn' : 'default'}>
            {r.bag === 'prohibited' ? 'CLOSED' : `Limit ${fmtBag(r.bag)}/day`}
          </Badge>
          {r.areaSpecific && <Badge tone="area">Area rule</Badge>}
        </div>
        {idNote && <p className="text-text-muted text-xs leading-relaxed">{idNote}</p>}
        {r.note && (
          <p className="text-xs leading-relaxed text-amber-700 dark:text-amber-400/90 border-t border-[rgba(16,42,66,0.07)] pt-2 mt-auto">
            {r.note}
          </p>
        )}
      </div>
    </div>
  );
}

function ShellfishCard({ species, area }: { species: ShellfishRule; area: string }) {
  const r = resolveShellfish(species, area);
  const idNote = ID_NOTES[species.common_name];
  const hasRules = !!(species.special_rules || r.note);
  return (
    <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] bg-surface-light dark:bg-surface overflow-hidden shadow-sm flex flex-col">
      <div className="aspect-[16/10] bg-[rgba(6,182,212,0.05)]">
        <SpeciesImage name={species.common_name} />
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div>
          <h3 className="font-display font-extrabold text-base leading-tight">{species.common_name}</h3>
          <p className="text-text-muted text-xs italic">{species.scientific_name}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={r.size === null ? 'warn' : 'default'}>
            {r.size === null ? 'No min size' : `Min ${r.size} mm`}
          </Badge>
          <Badge tone={r.bag === 'prohibited' ? 'warn' : 'default'}>
            {r.bag === 'prohibited' ? 'CLOSED' : `Limit ${fmtBag(r.bag)}/day`}
          </Badge>
          {r.areaSpecific && <Badge tone="area">Area rule</Badge>}
        </div>
        {r.season && <p className="text-xs text-text-muted">Season: {r.season}</p>}
        {idNote && <p className="text-text-muted text-xs leading-relaxed">{idNote}</p>}
        {hasRules && (
          <details className="text-xs mt-auto border-t border-[rgba(16,42,66,0.07)] pt-2">
            <summary className="cursor-pointer font-semibold text-amber-700 dark:text-amber-400">Special rules</summary>
            <p className="mt-1.5 leading-relaxed text-text-muted whitespace-pre-line">
              {[r.note, species.special_rules].filter(Boolean).join('\n')}
            </p>
          </details>
        )}
      </div>
    </div>
  );
}

function speciesPriority(area: string, name: string) {
  const featured = FEATURED_BY_AREA[area] || [];
  const idx = featured.indexOf(name);
  return idx === -1 ? 1000 : idx;
}

function areaSizeHighlights(area: string) {
  const all = [...FISH_RULES.finfish, ...FISH_RULES.shellfish];
  return (FEATURED_BY_AREA[area] || [])
    .map(name => {
      const species = all.find(s => s.common_name === name);
      if (!species) return null;
      if ('min_length_cm' in species) {
        const rules = resolveFinfish(species, area);
        return {
          name: species.common_name.replace('Groper (Hapuku)/Bass', 'Hapuku').replace('Spiny rock lobster (crayfish)', 'Crayfish'),
          size: rules.min === null ? 'No min' : `${rules.min} cm`,
          tone: rules.areaSpecific ? 'area' as const : 'default' as const,
        };
      }
      const rules = resolveShellfish(species, area);
      return {
        name: species.common_name.replace('Paua - Ordinary', 'Paua').replace('Oysters - Dredge', 'Oysters').replace('Spiny rock lobster (crayfish)', 'Crayfish'),
        size: rules.size === null ? 'No min' : `${rules.size} mm`,
        tone: rules.areaSpecific ? 'area' as const : 'default' as const,
      };
    })
    .filter((item): item is { name: string; size: string; tone: 'default' | 'area' } => Boolean(item))
    .slice(0, 4);
}

function FishingAreaMap({ area, onAreaChange }: { area: string; onAreaChange: (area: string) => void }) {
  const fmaRows = FISH_RULES.fma_guide;

  const getFmaRow = (key: string) => {
    const fmaCode = key.match(/FMA\d+(\/\d+)?/)?.[0];
    return fmaRows.find(row =>
      fmaCode ? row.fma.includes(fmaCode) : row.fma.toLowerCase().startsWith('kaikoura')
    );
  };

  return (
    <section className="rounded-3xl border border-[rgba(16,42,66,0.09)] bg-surface-light dark:bg-surface p-4 md:p-5 shadow-sm">
      <div className="flex flex-col gap-1 mb-4">
        <h2 className="font-display text-base md:text-lg font-extrabold">Fishing area map &amp; size guide</h2>
        <p className="text-text-muted text-xs md:text-sm leading-relaxed">
          Tap an area to switch the rules. Example sizes below are for common species in each area.
        </p>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-5 items-start">
        <div className="rounded-2xl border border-[rgba(16,42,66,0.08)] bg-[radial-gradient(circle_at_40%_20%,rgba(6,182,212,0.13),transparent_35%),rgba(79,179,217,0.04)] p-3">
          <svg viewBox="0 0 100 128" role="img" aria-label="Simplified New Zealand fishing management area map" className="w-full h-auto">
            <path d="M58 8 C68 12 74 21 72 32 C70 43 61 45 60 56 C59 66 67 70 64 79 C59 78 53 73 51 65 C48 53 41 49 39 39 C37 25 45 13 58 8Z" fill="rgba(6,182,212,0.18)" stroke="rgba(6,182,212,0.58)" strokeWidth="1.5" />
            <path d="M42 63 C49 71 48 82 42 90 C35 99 34 110 42 122 C29 117 22 106 24 94 C26 84 34 77 34 69 C34 63 38 61 42 63Z" fill="rgba(6,182,212,0.16)" stroke="rgba(6,182,212,0.58)" strokeWidth="1.5" />
            <path d="M46 118 C52 121 53 125 48 127 C43 126 40 124 40 121Z" fill="rgba(6,182,212,0.14)" stroke="rgba(6,182,212,0.42)" strokeWidth="1" />
            <path d="M75 81 C82 82 88 86 89 92 C83 92 77 89 73 85Z" fill="rgba(6,182,212,0.12)" stroke="rgba(6,182,212,0.38)" strokeWidth="1" />
            <text x="63" y="11" fontSize="4.3" fontWeight="800" className="fill-text-muted">North Island</text>
            <text x="15" y="90" fontSize="4.3" fontWeight="800" className="fill-text-muted">South Island</text>
            {AREA_MAP_POINTS.map(point => {
              const selected = point.key === area;
              return (
                <g
                  key={point.key}
                  role="button"
                  tabIndex={0}
                  aria-label={`Show rules for ${point.key}`}
                  onClick={() => onAreaChange(point.key)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onAreaChange(point.key);
                    }
                  }}
                  className="cursor-pointer outline-none"
                >
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={selected ? 5.2 : 4.1}
                    fill={selected ? '#06b6d4' : '#f8fafc'}
                    stroke={selected ? '#083344' : '#0891b2'}
                    strokeWidth={selected ? 1.8 : 1.1}
                  />
                  <text
                    x={point.x + 6}
                    y={point.y + 1.5}
                    className="fill-text-secondary-light dark:fill-text-secondary"
                    fontSize="4.5"
                    fontWeight="800"
                  >
                    {point.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {AREAS.map(a => {
            const selected = a.key === area;
            const row = getFmaRow(a.key);
            return (
              <button
                key={a.key}
                type="button"
                onClick={() => onAreaChange(a.key)}
                className={`text-left rounded-2xl border p-3 transition-colors ${
                  selected
                    ? 'border-accent bg-accent/8'
                    : 'border-[rgba(16,42,66,0.09)] hover:border-accent/45 hover:bg-accent/5'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-sm font-extrabold">{a.short}</div>
                    <div className="text-[0.65rem] font-bold uppercase tracking-wider text-accent">{AREA_MAP_POINTS.find(p => p.key === a.key)?.label}</div>
                  </div>
                  {selected && <span className="text-[0.65rem] font-bold text-accent">Selected</span>}
                </div>
                {row && <p className="mt-2 text-[0.72rem] leading-relaxed text-text-muted line-clamp-2">{row.covers}</p>}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {areaSizeHighlights(a.key).map(item => (
                    <span
                      key={`${a.key}-${item.name}`}
                      className={`rounded-full border px-2 py-1 text-[0.62rem] font-bold ${
                        item.tone === 'area'
                          ? 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                          : 'border-accent/18 bg-accent/8 text-text-secondary-light dark:text-text-secondary'
                      }`}
                    >
                      {item.name}: {item.size}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function FishGuide() {
  const [area, setArea] = useState<string>(() => {
    try { return localStorage.getItem('fish-guide-area') || AREAS[0].key; } catch { return AREAS[0].key; }
  });

  useEffect(() => {
    document.title = 'NZ Fish ID Chart & Catch Limits | MarineLink';
    setMeta('description', 'New Zealand fish identification chart with legal minimum sizes and daily catch limits for finfish and shellfish, by fishing area. Snapper, kingfish, blue cod, paua, crayfish and more.');
    setCanonical('https://marinelink.co.nz/fish-guide');
  }, []);

  useEffect(() => {
    try { localStorage.setItem('fish-guide-area', area); } catch { /* ignore */ }
  }, [area]);

  const combined = useMemo(
    () => FISH_RULES.combined_limits.filter(c => c.areas === area),
    [area]
  );

  const fmaRow = useMemo(() => {
    const fmaCode = area.match(/FMA\d+(\/\d+)?/)?.[0];
    return FISH_RULES.fma_guide.find(f =>
      fmaCode ? f.fma.includes(fmaCode) : f.fma.toLowerCase().startsWith('kaikoura')
    );
  }, [area]);

  const featuredSpecies = useMemo(() => {
    const all = [...FISH_RULES.finfish, ...FISH_RULES.shellfish];
    return (FEATURED_BY_AREA[area] || [])
      .map(name => all.find(s => s.common_name === name))
      .filter((s): s is FinfishRule | ShellfishRule => Boolean(s));
  }, [area]);

  const finfish = useMemo(
    () => [...FISH_RULES.finfish].sort((a, b) => {
      const pa = speciesPriority(area, a.common_name);
      const pb = speciesPriority(area, b.common_name);
      return pa === pb ? a.common_name.localeCompare(b.common_name) : pa - pb;
    }),
    [area]
  );

  const shellfish = useMemo(
    () => [...FISH_RULES.shellfish].sort((a, b) => {
      const pa = speciesPriority(area, a.common_name);
      const pb = speciesPriority(area, b.common_name);
      return pa === pb ? a.common_name.localeCompare(b.common_name) : pa - pb;
    }),
    [area]
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 md:py-12 flex flex-col gap-8">
      {/* Hero */}
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs font-bold text-accent uppercase tracking-wider">
          <span>🎣</span> Recreational fishing rules
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold leading-tight">
          NZ Fish ID Chart &amp; Catch Limits
        </h1>
        <p className="text-text-muted text-sm md:text-base max-w-2xl leading-relaxed">
          Identify your catch and check the legal minimum size and daily bag limit for your fishing area —
          finfish, shellfish and rock lobster. Rules differ by area, so pick yours below.
        </p>
      </header>

      {/* Area selector */}
      <section className="rounded-3xl border border-[rgba(16,42,66,0.09)] bg-surface-light dark:bg-surface p-4 md:p-5 shadow-sm">
        <h2 className="font-display text-sm font-extrabold mb-3">Your fishing area</h2>
        <div className="flex flex-wrap gap-2">
          {AREAS.map(a => (
            <button
              key={a.key}
              onClick={() => setArea(a.key)}
              className={`px-3.5 py-2 rounded-full text-xs font-bold border transition-colors ${
                area === a.key
                  ? 'bg-accent text-white border-accent'
                  : 'bg-transparent text-text-muted border-[rgba(16,42,66,0.15)] hover:border-accent/50 hover:text-accent'
              }`}
            >
              {a.short}
            </button>
          ))}
        </div>
        {fmaRow && (
          <p className="text-text-muted text-xs mt-3 leading-relaxed">
            <span className="font-semibold">{fmaRow.fma}:</span> {fmaRow.covers}
          </p>
        )}
      </section>

      {/* Combined limits */}
      {combined.length > 0 && (
        <section className="rounded-2xl border border-cyan-500/25 bg-cyan-500/5 p-4 md:p-5">
          <h2 className="font-display text-sm font-extrabold mb-2">Combined daily limits — {AREAS.find(a => a.key === area)?.short}</h2>
          <ul className="text-xs md:text-sm text-text-muted leading-relaxed list-disc pl-5 space-y-1">
            {combined.map((c, i) => <li key={i}>{c.rule}</li>)}
          </ul>
        </section>
      )}

      <FishingAreaMap area={area} onAreaChange={setArea} />

      {featuredSpecies.length > 0 && (
        <section>
          <div className="flex items-baseline justify-between gap-3 mb-4">
            <h2 className="font-display text-xl md:text-2xl font-extrabold">
              Popular around {AREAS.find(a => a.key === area)?.short}
            </h2>
            <span className="text-text-muted text-xs">shown first below</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {featuredSpecies.map(s => (
              <div key={s.common_name} className="rounded-2xl border border-[rgba(16,42,66,0.09)] bg-surface-light dark:bg-surface overflow-hidden shadow-sm">
                <div className="aspect-[16/11] bg-[rgba(6,182,212,0.05)]">
                  <SpeciesImage name={s.common_name} />
                </div>
                <div className="p-3">
                  <p className="font-display text-xs font-extrabold leading-tight">{s.common_name}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Finfish */}
      <section>
        <div className="flex items-baseline justify-between gap-3 mb-4">
          <h2 className="font-display text-xl md:text-2xl font-extrabold">Finfish</h2>
          <span className="text-text-muted text-xs">{FISH_RULES.finfish.length} species</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {finfish.map(s => <FinfishCard key={s.common_name} species={s} area={area} />)}
        </div>
      </section>

      {/* Shellfish */}
      <section>
        <div className="flex items-baseline justify-between gap-3 mb-4">
          <h2 className="font-display text-xl md:text-2xl font-extrabold">Shellfish &amp; Rock Lobster</h2>
          <span className="text-text-muted text-xs">{FISH_RULES.shellfish.length} species</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shellfish.map(s => <ShellfishCard key={s.common_name} species={s} area={area} />)}
        </div>
      </section>

      {/* Tides cross-link */}
      <Link
        to="/tides"
        className="rounded-2xl border border-accent/25 bg-accent/5 p-4 md:p-5 flex items-center gap-4 hover:bg-accent/10 transition-colors group"
      >
        <span className="text-3xl">🌊</span>
        <div className="flex-1">
          <p className="font-display font-extrabold text-sm md:text-base group-hover:text-accent transition-colors">
            Plan the trip — tide times &amp; bite times
          </p>
          <p className="text-text-muted text-xs md:text-sm">Check today's tides and the best fishing times for your port.</p>
        </div>
        <span className="text-accent font-bold">→</span>
      </Link>

      {/* Disclaimer */}
      <section className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 md:p-5">
        <h2 className="font-display text-sm font-extrabold mb-2">⚖️ General guide only — check before you fish</h2>
        <p className="text-xs md:text-sm text-text-muted leading-relaxed">
          This chart summarises New Zealand recreational fishing rules from official MPI sources (fish.govt.nz regional
          pages and the Fisheries Notice No.2 2025), compiled August 2026. Rules change, and local closures
          (rāhui, mātaitai reserves, taiāpure) can override the limits shown here. The legal minimum size and bag limit
          for your exact location is what counts — check the official sources before you fish:
        </p>
        <div className="flex flex-wrap gap-3 mt-3 text-xs font-bold">
          <a href="https://fish.govt.nz" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
            fish.govt.nz — NZ Fishing Rules →
          </a>
          <a href="https://www.mpi.govt.nz/fishing-aquaculture/recreational-fishing" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
            MPI recreational fishing →
          </a>
        </div>
        <p className="text-[0.65rem] text-text-muted mt-3">
          Data: {FISH_RULES.finfish.length} finfish + {FISH_RULES.shellfish.length} shellfish species · Generated {FISH_RULES.generated} · Fines apply for undersize or over-limit catch.
        </p>
      </section>
    </div>
  );
}
